import { KeyRound, Server, ShieldCheck, StickyNote, Puzzle, Database } from 'lucide-react';

/**
 * Create / edit modal for vault items. Two steps on create (type picker as
 * icon cards → per-type fields, mirroring the DB Manager connection flow);
 * editing jumps straight to the form. Saving emits vaultStore.putItem —
 * the Rust side derives the plaintext `index` from `data`, so this modal
 * only ships { id?, type, name, sync?, favorite?, data }.
 */

// Shared type metadata (VaultView imports this for list/detail icons).
export const VAULT_TYPE_ORDER = ['password', 'sftp', 'dbConnection', 'authHeader', 'note', 'custom'];

export const VAULT_TYPE_META = {
  password: { labelKey: 'vault.typePassword', Icon: KeyRound },
  sftp: { labelKey: 'vault.typeSftp', Icon: Server },
  dbConnection: { labelKey: 'vault.typeDbConnection', Icon: Database },
  authHeader: { labelKey: 'vault.typeAuthHeader', Icon: ShieldCheck },
  note: { labelKey: 'vault.typeNote', Icon: StickyNote },
  custom: { labelKey: 'vault.typeCustom', Icon: Puzzle },
};

export const DEFAULT_PORTS = { sftp: 22, ftp: 21, ftps: 21 };

// DB Manager's own engines. Kept separate from DEFAULT_PORTS because the two
// pickers are unrelated — an sftp item switching protocol must not inherit a
// database port, and vice versa.
//
// These are exactly the engines db-manager implements (entry.js ENGINES, and
// src-tauri/src/db_{mysql,postgres,sqlite}.rs). An engine offered here that the
// extension cannot open is a connection the user can save and never use.
// sqlite is absent from this map on purpose: it is a FILE, with no host, port
// or credentials.
export const DB_DEFAULT_PORTS = { mysql: 3306, postgres: 5432 };

export const isFileEngine = (engine) => engine === 'sqlite';

// Anything bigger than this is not a private key (real ones are a few KB) —
// refuse it instead of silently slurping a wrong pick into the vault.
export const MAX_KEY_FILE_BYTES = 256 * 1024;

// Passkey state lives in `data.passkeys` / `data.passkeysDeleted`. vault_get_item
// hands those records over with their key material removed, so anything the
// webview sends back would be a stripped copy — echoing it would erase the real
// keys. Every `data` written from this side therefore omits both keys entirely.
// Preserving them across a save is vault_put_item's job, and lands with the code
// that first creates a credential: absent will mean "no change", and it will
// re-attach the stored arrays before encrypting. Until then nothing writes a
// passkey, so there is none to preserve.
// Note: an sftp item's TOP-LEVEL `data.privateKey` is the SSH key and is
// unrelated — it is a normal form field and is written as usual.
export const PASSKEY_DATA_KEYS = ['passkeys', 'passkeysDeleted'];

/** Shallow copy of `data` without the passkey keys (see PASSKEY_DATA_KEYS). */
export function omitPasskeyKeys(data) {
  const out = { ...(data || {}) };
  for (const k of PASSKEY_DATA_KEYS) delete out[k];
  return out;
}

// The `data` keys each item type's form actually owns — the ones buildKnownData
// rebuilds from scratch on every save. Everything else in `data` is carried
// through verbatim in __rest, so saving never destroys a key this form has no
// field for.
//
// Keyed by type on purpose, because ownership is per-type: `fields` belongs to a
// custom item, but on a password item it is just a key nobody here edits, and
// the password arm would drop it. An unrecognised type owns nothing, so its
// whole `data` is carried.
//
// A key listed for a type is dropped when that type's arm does not emit it —
// which is how switching an sftp item between password and key auth discards the
// credential it no longer uses.
// `folder` is owned by EVERY type — see FOLDER_KEY below.
export const FOLDER_KEY = 'folder';

export const OWNED_DATA_KEYS = {
  password: [FOLDER_KEY, 'url', 'username', 'password', 'totp', 'notes'],
  sftp: [
    FOLDER_KEY,
    'protocol',
    'host',
    'port',
    'username',
    'authMethod',
    'privateKey',
    'passphrase',
    'initialPath',
    'password',
    'notes',
  ],
  dbConnection: [FOLDER_KEY, 'engine', 'host', 'port', 'database', 'username', 'password', 'ssl', 'path', 'notes'],
  authHeader: [FOLDER_KEY, 'headerName', 'headerValue', 'sourceEnv', 'notes'],
  note: [FOLDER_KEY, 'body'],
  custom: [FOLDER_KEY, 'fields', 'notes'],
};

/** 20 chars from letters/digits/symbols via crypto.getRandomValues. */
export function generatePassword(length = 20) {
  const alphabet =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+[]{}<>?';
  const nums = new Uint32Array(length);
  crypto.getRandomValues(nums);
  return Array.from(nums, (n) => alphabet[n % alphabet.length]).join('');
}

export function emptyValues() {
  return {
    name: '',
    // password
    url: '',
    username: '',
    password: '',
    totp: '',
    // sftp
    protocol: 'sftp',
    host: '',
    port: String(DEFAULT_PORTS.sftp),
    authMethod: 'password',
    privateKey: '',
    passphrase: '',
    initialPath: '',
    // dbConnection
    engine: 'mysql',
    database: '',
    ssl: false,
    path: '',
    // folders — every type
    folder: '',
    // authHeader
    headerName: '',
    headerValue: '',
    sourceEnv: '',
    // note
    body: '',
    // custom
    fields: [{ k: '', v: '', secret: false }],
    // shared
    notes: '',
    // Untouched `data` keys this form has no field for (never passkey state).
    __rest: {},
  };
}

/** The `data` keys this type's form does not own — kept as-is and written back. */
export function restFromData(d, type) {
  const owned = new Set(OWNED_DATA_KEYS[type] || []);
  const rest = {};
  for (const [k, v] of Object.entries(d)) {
    if (owned.has(k) || PASSKEY_DATA_KEYS.includes(k)) continue;
    rest[k] = v;
  }
  return rest;
}

export function valuesFromItem(item) {
  const d = item?.data || {};
  const base = emptyValues();
  return {
    ...base,
    name: item?.name || '',
    url: d.url || '',
    username: d.username || '',
    password: d.password || '',
    totp: d.totp || '',
    protocol: d.protocol || 'sftp',
    host: d.host || '',
    port:
      d.port != null
        ? String(d.port)
        : String(
            // An item carries one port, but which default it falls back to
            // depends on the type — 22 for sftp, 3306/5432 for a database.
            DB_DEFAULT_PORTS[d.engine] || DEFAULT_PORTS[d.protocol] || DEFAULT_PORTS.sftp
          ),
    engine: d.engine || 'mysql',
    database: d.database || '',
    ssl: !!d.ssl,
    path: d.path || '',
    folder: typeof d.folder === 'string' ? d.folder : '',
    authMethod: d.authMethod || 'password',
    privateKey: d.privateKey || '',
    passphrase: d.passphrase || '',
    initialPath: d.initialPath || '',
    headerName: d.headerName || '',
    headerValue: d.headerValue || '',
    sourceEnv: d.sourceEnv || '',
    body: d.body || '',
    fields:
      Array.isArray(d.fields) && d.fields.length > 0
        ? d.fields.map((f) => ({ k: f.k || '', v: f.v || '', secret: !!f.secret }))
        : base.fields,
    notes: d.notes || '',
    __rest: restFromData(d, item?.type),
  };
}

/** Drop empty-string optional keys so `data` stays minimal. */
export function prune(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v == null) continue;
    out[k] = v;
  }
  return out;
}

export function buildKnownData(type, v) {
  switch (type) {
    case 'password':
      return {
        ...prune({ url: v.url.trim(), username: v.username.trim(), totp: v.totp, notes: v.notes }),
        password: v.password,
      };
    case 'sftp': {
      const data = {
        protocol: v.protocol,
        host: v.host.trim(),
        port: parseInt(v.port, 10) || DEFAULT_PORTS[v.protocol] || DEFAULT_PORTS.sftp,
        username: v.username.trim(),
        authMethod: v.protocol === 'sftp' ? v.authMethod : 'password',
        ...prune({ initialPath: v.initialPath.trim(), notes: v.notes }),
      };
      if (v.authMethod === 'key' && v.protocol === 'sftp') {
        data.privateKey = v.privateKey;
        if (v.passphrase) data.passphrase = v.passphrase;
      } else {
        data.password = v.password;
      }
      return data;
    }
    case 'dbConnection':
      // sqlite is a file on disk. Emitting host/port/username for it would put
      // fields in the vault that mean nothing and that DB Manager would have to
      // learn to ignore.
      if (isFileEngine(v.engine)) {
        return {
          engine: v.engine,
          path: v.path.trim(),
          ...prune({ notes: v.notes }),
        };
      }
      return {
        engine: v.engine,
        host: v.host.trim(),
        port: parseInt(v.port, 10) || DB_DEFAULT_PORTS[v.engine] || 3306,
        database: v.database.trim(),
        username: v.username.trim(),
        password: v.password,
        // Only when true: `prune` drops empties, and a literal `ssl: false` on
        // every row is noise the DB Manager would have to ignore anyway.
        ...(v.ssl ? { ssl: true } : {}),
        ...prune({ notes: v.notes }),
      };
    case 'authHeader':
      return {
        headerName: v.headerName.trim(),
        headerValue: v.headerValue,
        ...prune({ sourceEnv: v.sourceEnv, notes: v.notes }),
      };
    case 'note':
      return { body: v.body };
    case 'custom':
      return {
        fields: v.fields
          .filter((f) => f.k.trim() !== '' || f.v !== '')
          .map((f) => ({ k: f.k.trim(), v: f.v, secret: !!f.secret })),
        ...prune({ notes: v.notes }),
      };
    default:
      return {};
  }
}

/**
 * `data` for one save: the unknown keys first so every rebuilt form field wins
 * on a collision. __rest deliberately skips prune() — an unknown key holding
 * '' or null must survive byte-identically — and the passkey keys are dropped
 * unconditionally, whatever __rest happens to hold.
 */
export function buildData(type, v) {
  return buildDataInner(type, v);
}

/**
 * The `data` body written for a TEAM item.
 *
 * Identical to a personal item's, minus the personal `folder` key. Team folder
 * membership is a real server row referenced by `folder_id`, so writing a
 * `folder` name into the body too would create a second, divergent source of
 * truth — in a field the server can read, and which the folder-permission check
 * does not consult. One place decides which folder a team item is in, and it is
 * `folder_id`.
 */
export function buildTeamData(type, v) {
  return buildDataInner(type, { ...v, folder: '' });
}

export function buildDataInner(type, v) {
  // `folder` is emitted here rather than in every arm of `buildKnownData`,
  // because it belongs to no type in particular — it is how the user organises
  // the vault, orthogonal to what a given item stores. Trimmed and dropped when
  // empty so "no folder" is a genuinely absent key, matching what
  // `derive_index` does on the Rust side.
  const folder = typeof v.folder === 'string' ? v.folder.trim() : '';
  return omitPasskeyKeys({
    ...(v.__rest || {}),
    ...buildKnownData(type, v),
    ...(folder ? { folder } : {}),
  });
}

export function isValid(type, v) {
  if (!v.name.trim()) return false;
  if (type === 'sftp') return v.host.trim() !== '' && v.username.trim() !== '';
  if (type === 'dbConnection')
    return isFileEngine(v.engine)
      ? v.path.trim() !== ''
      : v.host.trim() !== '' && v.username.trim() !== '' && v.database.trim() !== '';
  if (type === 'authHeader') return v.headerName.trim() !== '';
  return true;
}
